/**
 * The conversation DEEP REPORT, rendered INSIDE the AVALIAÇÃO DA FONTE pane.
 *
 * `assess-content` writes a per-thread analysis of a compound WhatsApp
 * conversation to `sources/whatsapp/<stem>.assessment.md`. Before this it had
 * no UI at all — the operator hit the gap live: the artifact existed on disk
 * and nothing in LexIDE could show it.
 *
 * Operator ruling 2026-08-11 (relayed by `mission-ingest`, handshake
 * `20260811T160519Z`): it is NOT a separate viewer door. It renders here, in
 * the pane, directly under ANÁLISE AUTOMÁTICA — one place to read what the
 * machine made of a source.
 *
 * ## Why the markdown is RENDERED and not PARSED
 *
 * The producer's body is threads + gaps (`## Tópicos identificados (N)`, then a
 * `### N. <topic> — relevância <label>` per thread, closing with an optional
 * `## Lacunas`). It would be easy to parse that into structured pane rows —
 * and wrong. The format was being slimmed the same hour this was built
 * (`mission-ingest` `97bcb09bf` dropped the title, summary, rationale and
 * metadata header), so a parser would be a closed map over a format its author
 * is actively changing. Presentation stays with the PRODUCER: they decide what
 * the report says and how it is sectioned; this styles it.
 *
 * Anything semantic — collapsible threads, `[dd/mm hh:mm]` anchors that jump
 * into the conversation page — is a CONTRACT conversation with `mission-ingest`
 * (the way the media tokens were), never pattern-matching their prose.
 *
 * ## Absent is not unreadable
 *
 * A conversation assessed before this feature has no report, and the pane must
 * look exactly as it did — no heading, no empty state, nothing. But a read that
 * FAILS is a different answer, and showing silence for it would tell a lawyer
 * "there is no deeper analysis" when the truth is "I could not look".
 * `useFileText` keeps the two apart on the gateway's error CODE.
 *
 * ⚠ **No `rehypeRaw` here, deliberately.** The report quotes the lawyer's own
 * message text verbatim, so any HTML a correspondent ever typed into a WhatsApp
 * message travels inside it. react-markdown escapes HTML by default; adding
 * `rehypeRaw` to "fix rendering" would turn quoted evidence into live markup.
 */

import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { deepReportPathFor } from "@/lib/workspace/conversation-deep-report";
import { useIsCompoundHead } from "@/hooks/useIsCompoundHead";
import { useFileText } from "@/hooks/useFileText";
import { cn } from "@/lib/utils";
import type { FileRef } from "@/stores/useWorkspaceFileStore";

export interface DeepReportSectionProps {
  file: FileRef;
  /** The pane's reading mode — expanded steps up to reading typography. */
  expanded: boolean;
}

export function DeepReportSection({ file, expanded }: DeepReportSectionProps) {
  const { t } = useTranslation();
  // ⚠ Head-ness comes from the catalog's `node_id` via the shared hook — the
  // same single fact that decides the Acervo's nesting and what the head
  // OPENS. A filename pattern here would be a third rule free to disagree with
  // the other two.
  const isHead = useIsCompoundHead(file);
  // Null path ⇒ no read is issued. Only a compound head ever asks.
  const { state, text } = useFileText(isHead ? deepReportPathFor(file.path) : null);

  // Not a conversation head, or genuinely no report: the pane is exactly what
  // it was before this feature existed. This is the common case and it must
  // stay completely silent — no heading, no placeholder.
  //
  // ⚠ An EMPTY report counts as absent. A zero-byte or whitespace-only
  // `.assessment.md` reads as `present` with nothing in it, and rendering a
  // heading over emptiness tells the lawyer an analysis exists when none does.
  if (!isHead || state === "absent") return null;
  if (state === "present" && !text?.trim()) return null;

  // Deliberately silent while loading too: the report arrives a beat after the
  // pane, and a spinner that flashes on every conversation would be noise
  // announcing something the lawyer did not ask for.
  if (state === "loading") return null;

  if (state === "unreadable") {
    return (
      <section
        data-testid="source-assessment-deep-report"
        data-deep-report-state="unreadable"
        className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-300/60 bg-amber-50/60 px-2.5 py-2 text-[12px] text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/15 dark:text-amber-400"
      >
        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
        <p>{t("assessment.deepReport.unreadable")}</p>
      </section>
    );
  }

  return (
    <section
      data-testid="source-assessment-deep-report"
      data-deep-report-state="present"
      className={cn(
        "mb-2 rounded-md border border-slate-200/80 bg-white/60 px-2.5 py-2 dark:border-coffee-700/50 dark:bg-coffee-800/40",
        expanded ? "text-[14px]" : "text-[12px]",
      )}
    >
      <h4 className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-cream-400">
        {t("assessment.deepReport.title")}
      </h4>
      {/* The producer's own headings would render at DOCUMENT scale inside a
          bottom strip, so every level is pulled down explicitly.
          ⚠ `prose-sm` is the smallest size Tailwind Typography actually ships.
          The first cut used `prose-xs`, which does not exist — so it silently
          did nothing and the collapsed pane rendered at full document scale.
          A class that is not real fails by looking like a class that is.
          ⚠ `h1` is styled too even though the current producer starts at `h2`:
          reports written before the threads-only slimming still carry a `#`
          title, and an un-tamed one renders larger than the case name. */}
      <div
        className={cn(
          "prose prose-sm prose-slate max-w-none dark:prose-invert",
          expanded ? "text-[14px]" : "text-[12px]",
          "prose-headings:font-sans prose-headings:tracking-wide",
          "prose-h1:mb-1 prose-h1:mt-0 prose-h1:text-[13px] prose-h1:font-semibold prose-h1:normal-case",
          "prose-h2:mb-1 prose-h2:mt-3 prose-h2:text-[11px] prose-h2:font-semibold prose-h2:uppercase prose-h2:text-slate-500 dark:prose-h2:text-cream-400",
          "prose-h3:mb-0.5 prose-h3:mt-2.5 prose-h3:text-[12px] prose-h3:font-semibold prose-h3:normal-case",
          "prose-p:my-1 prose-li:my-0 prose-ul:my-1",
          "prose-blockquote:my-1 prose-blockquote:border-l-2 prose-blockquote:py-0 prose-blockquote:pl-2 prose-blockquote:font-normal prose-blockquote:not-italic",
          // The section title sits directly above the first heading.
          "prose-headings:first:mt-0",
        )}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    </section>
  );
}
